import { describeError, MessageType, type InputCommand, type SignalMessage } from './protocol.js';

/**
 * Used only if the server sends no ICE configuration, which means an outdated
 * server or a broken config. STUN-only works on a LAN and on cooperative home
 * NATs, and fails on carrier-grade NAT, so a session that lands here is unlikely
 * to connect over mobile data.
 *
 * The real configuration, TURN credentials included, arrives with the `joined`
 * message. Credentials are never checked into the client for the same reason
 * they are not compiled into the APK.
 */
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
];

export interface ViewerCallbacks {
  /** The screen as a WebRTC track (MediaProjection backend). */
  onTrack: (stream: MediaStream) => void;
  /** The device camera track. */
  onCameraTrack?: (stream: MediaStream) => void;
  /** The device microphone track. */
  onMicTrack?: (stream: MediaStream) => void;
  /** scrcpy mode: the phone streams encoded H.264 on this channel instead of a track. */
  onVideoDataChannel?: (channel: RTCDataChannel) => void;
  /** Remote terminal. Opened for every session, whichever capture backend is in use. */
  onShellDataChannel?: (channel: RTCDataChannel) => void;
  /** Bulk file transfer with the device. */
  onFilesDataChannel?: (channel: RTCDataChannel) => void;
  /** Session control: stream on/off and the connection indicator. */
  onControlDataChannel?: (channel: RTCDataChannel) => void;
  /** Input audit log: captured text entries, history then live. */
  onAuditDataChannel?: (channel: RTCDataChannel) => void;
  onStatus: (text: string, isError?: boolean) => void;
  onClosed: () => void;
}

/**
 * Viewer side of the session.
 *
 * The phone is the offerer, so this class never calls createOffer — it waits for
 * an offer, answers it, and receives the data channel via ondatachannel. That
 * asymmetry is intentional; see docs/architecture.md.
 */
/**
 * How long to wait after asking to reach a device before giving up. The server
 * already drops a stranded viewer at 30s (a woken device that never hosts), so this
 * is a longer backstop for a socket that goes quiet with no error at all.
 */
const CONNECT_TIMEOUT_MS = 60_000;

export class ViewerSession {
  private socket: WebSocket | null = null;
  private pc: RTCPeerConnection | null = null;
  private channel: RTCDataChannel | null = null;

  /** ICE that arrives before setRemoteDescription; addIceCandidate would throw. */
  private readonly pendingCandidates: RTCIceCandidateInit[] = [];
  private remoteSet = false;

  /** Backstop timer for the unattended path; cleared once the device responds. */
  private connectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly callbacks: ViewerCallbacks) {}

  /**
   * Join a live attended session by its code, or reach an enrolled device by its
   * permanent code when `unattended` is set. The two differ only in the first frame
   * sent; everything after `joined` is identical, because the server drives the
   * device to host and then attaches this viewer exactly as an attended join would.
   */
  connect(serverUrl: string, sessionId: string, pin: string, unattended = false): void {
    const socket = new WebSocket(serverUrl);
    this.socket = socket;

    socket.onopen = () => {
      if (unattended) {
        this.callbacks.onStatus('Reaching the device…');
        this.send({ type: MessageType.CONNECT_DEVICE, code: sessionId, pin });
        this.armConnectTimeout();
      } else {
        this.callbacks.onStatus('Joining session…');
        this.send({ type: MessageType.JOIN, sessionId, pin });
      }
    };

    socket.onmessage = (event) => {
      let message: SignalMessage;
      try {
        message = JSON.parse(event.data as string) as SignalMessage;
      } catch {
        return;
      }
      void this.handle(message);
    };

    socket.onerror = () => {
      this.callbacks.onStatus(`Could not reach ${serverUrl}.`, true);
    };

    socket.onclose = () => {
      this.socket = null;
    };
  }

  private async handle(message: SignalMessage): Promise<void> {
    switch (message.type) {
      case MessageType.JOINED:
        // The device is hosting and this viewer is attached, so it is going to
        // stream. Cancel the unattended backstop; a slow host is expected now.
        this.clearConnectTimeout();
        this.callbacks.onStatus('Waiting for the phone to start streaming…');
        // TURN credentials arrive here, so the peer connection cannot be built
        // before this message lands.
        this.createPeerConnection(message.iceServers, message.iceTransportPolicy);
        break;

      case MessageType.WAKING:
        // Unattended only: the device was offline and is being woken. Keep waiting;
        // the server sends `joined` once it announces and hosts.
        this.callbacks.onStatus('Waking the device, this can take a moment…');
        break;

      case MessageType.SIGNAL:
        await this.handleSignal(message);
        break;

      case MessageType.PEER_LEFT:
        this.callbacks.onStatus('The phone ended the session.', true);
        this.close();
        break;

      case MessageType.ERROR:
        this.callbacks.onStatus(describeError(message.reason), true);
        this.close();
        break;
    }
  }

  private async handleSignal(message: SignalMessage): Promise<void> {
    const pc = this.pc;
    if (!pc) return;

    if (message.sdp && message.sdpType === 'offer') {
      await pc.setRemoteDescription({ type: 'offer', sdp: message.sdp });
      this.remoteSet = true;

      // Anything buffered while we were waiting for the offer.
      for (const candidate of this.pendingCandidates) {
        await pc.addIceCandidate(candidate).catch(() => undefined);
      }
      this.pendingCandidates.length = 0;

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.send({
        type: MessageType.SIGNAL,
        sdp: answer.sdp,
        sdpType: 'answer',
      });
      return;
    }

    if (message.candidate) {
      const candidate: RTCIceCandidateInit = {
        candidate: message.candidate,
        sdpMid: message.sdpMid,
        sdpMLineIndex: message.sdpMLineIndex,
      };
      if (this.remoteSet) {
        // Failures here are routine (a candidate for a bundled transport we
        // already have) and not worth surfacing.
        await pc.addIceCandidate(candidate).catch(() => undefined);
      } else {
        this.pendingCandidates.push(candidate);
      }
    }
  }

  private createPeerConnection(
    iceServers?: RTCIceServer[],
    iceTransportPolicy?: RTCIceTransportPolicy,
  ): void {
    const servers = iceServers?.length ? iceServers : FALLBACK_ICE_SERVERS;
    const relayOnly = iceTransportPolicy === 'relay';

    const turnCount = servers.filter((s) =>
      (Array.isArray(s.urls) ? s.urls : [s.urls]).some((u) => u.startsWith('turn')),
    ).length;
    console.info(
      `peer connection: ${servers.length} ICE server entries, ${turnCount} with TURN, relayOnly=${relayOnly}`,
    );

    const pc = new RTCPeerConnection({
      iceServers: servers,
      bundlePolicy: 'max-bundle',
      // 'relay' discards host and reflexive candidates so only TURN can succeed.
      // A connection that succeeds with STUN available proves nothing about TURN.
      iceTransportPolicy: relayOnly ? 'relay' : 'all',
    });
    this.pc = pc;

    pc.ontrack = (event) => {
      const stream = event.streams[0];
      if (!stream) return;
      // The host tags each track with a stream id so they can be told apart: camera and mic
      // are added on demand, the screen (MediaProjection backend) uses its own id.
      if (stream.id === 'camera') {
        this.callbacks.onCameraTrack?.(stream);
      } else if (stream.id === 'mic') {
        this.callbacks.onMicTrack?.(stream);
      } else {
        this.callbacks.onTrack(stream);
      }
    };

    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      this.send({
        type: MessageType.SIGNAL,
        candidate: event.candidate.candidate,
        sdpMid: event.candidate.sdpMid ?? undefined,
        sdpMLineIndex: event.candidate.sdpMLineIndex ?? undefined,
      });
    };

    // The phone creates the channels, so they arrive here rather than being opened.
    // `input` carries control both ways; `video` (scrcpy mode only) carries H.264.
    pc.ondatachannel = (event) => {
      if (event.channel.label === 'video') {
        event.channel.binaryType = 'arraybuffer';
        this.callbacks.onVideoDataChannel?.(event.channel);
      } else if (event.channel.label === 'shell') {
        event.channel.binaryType = 'arraybuffer';
        // The phone starts its shell when this channel opens, so hand it over then.
        if (event.channel.readyState === 'open') {
          this.callbacks.onShellDataChannel?.(event.channel);
        } else {
          event.channel.onopen = () => this.callbacks.onShellDataChannel?.(event.channel);
        }
      } else if (event.channel.label === 'files') {
        event.channel.binaryType = 'arraybuffer';
        this.callbacks.onFilesDataChannel?.(event.channel);
      } else if (event.channel.label === 'control') {
        this.callbacks.onControlDataChannel?.(event.channel);
      } else if (event.channel.label === 'audit') {
        this.callbacks.onAuditDataChannel?.(event.channel);
      } else if (event.channel.label === 'adb') {
        // The native adb tunnel is for the standalone proxy tool, not the browser.
        event.channel.close();
      } else {
        this.channel = event.channel;
      }
    };

    pc.onconnectionstatechange = () => {
      switch (pc.connectionState) {
        case 'connected':
          this.callbacks.onStatus('Connected');
          break;
        case 'failed':
          // STUN-only ICE: this is almost always symmetric NAT on one end.
          this.callbacks.onStatus('Connection failed. A TURN relay is likely needed.', true);
          this.close();
          break;
        case 'disconnected':
          this.callbacks.onStatus('Connection lost, retrying…', true);
          break;
      }
    };
  }

  private armConnectTimeout(): void {
    this.clearConnectTimeout();
    this.connectTimer = setTimeout(() => {
      this.callbacks.onStatus('The device did not respond. It may be offline.', true);
      this.close();
    }, CONNECT_TIMEOUT_MS);
  }

  private clearConnectTimeout(): void {
    if (this.connectTimer !== null) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
  }

  /** Fire and forget. Input is high rate; a dropped event is not worth a round trip. */
  sendInput(command: InputCommand): void {
    if (this.channel?.readyState !== 'open') return;
    this.channel.send(JSON.stringify(command));
  }

  get canSendInput(): boolean {
    return this.channel?.readyState === 'open';
  }

  private send(message: SignalMessage): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  close(): void {
    this.clearConnectTimeout();
    this.channel?.close();
    this.channel = null;
    this.pc?.close();
    this.pc = null;
    this.socket?.close(1000, 'viewer closed');
    this.socket = null;
    this.remoteSet = false;
    this.pendingCandidates.length = 0;
    this.callbacks.onClosed();
  }
}
